import { TypeSystem } from "../../support/type-system.support";
import { OrderEventItems } from "./order-event-items.model";
import { OrderEventOriginType } from "./order-event-origin-type.enum";
import { OrderEventProducts } from "./order-event-products.model";
import { OrderEventStatus } from "./order-event-status.enum";
import { OrderEventTracking } from "./order-event-tracking.model";
import { ReplacementItem } from "./replacement-item.model";

	/**
	 * @description
	 *  Modelo de una nota de CyR (cambio y reclamo), o NC (nota de crédito),
	 *	sobre un pedido.
	 *
	 * @see gigot-backend/Models/Orders/OrderEvent.cs
	 */

export class OrderEvent {

	public active: boolean;
	public creationDate: Date;
	public generateCreditNote: boolean;
	public id: number;
	public orderEventItems: OrderEventItems[];
	public orderEventOriginType: OrderEventOriginType;
	public orderEventProducts: OrderEventProducts[];
	public orderEventResultId: number;
	public orderEventStatus: OrderEventStatus;
	public orderEventTrackings: OrderEventTracking[];
	public orderEventTypeId: number;
	public orderId: number;
	public replacementItems: ReplacementItem[];

	/**
	 * @description
	 *	Construye una nota con una semántica vacía. Nótese que no es lo mismo
	 *	que construir una nota con valores por defecto. Las reglas generales
	 *	de construcción son:
	 *
	 *      - Campos 'array' se definen como [].
	 *      - Campos 'boolean' se definen como 'false'.
	 *      - Campos 'Date' se definen como 'undefined'.
	 *      - Campos 'enum' se definen como 'undefined'.
	 *      - Campos 'number' se definen como 'undefined'.
	 *      - Campos 'string' se definen como '' (cadena vacía).
	 *      - Campos 'object' se definen llamando a su propio método #empty().
	 *      - Campos de la superclase se definen al igual que campos 'object'.
	 *      - Campos genéricos se definen como 'undefined'.
	 *		- Campos recursivos se definen como 'undefined'.
	 *
	 * @returns
	 *	Una nota de CyR/NC vacía.
	 */
	public static empty(): OrderEvent {
		const orderEvent: OrderEvent = new OrderEvent();
		orderEvent.active = false;
		orderEvent.creationDate = undefined;
		orderEvent.generateCreditNote = false;
		orderEvent.id = undefined;
		orderEvent.orderEventItems = [];
		orderEvent.orderEventOriginType = undefined;
		orderEvent.orderEventProducts = [];
		orderEvent.orderEventResultId = undefined;
		orderEvent.orderEventStatus = undefined;
		orderEvent.orderEventTrackings = [];
		orderEvent.orderEventTypeId = undefined;
		orderEvent.orderId = undefined;
		orderEvent.replacementItems = [];
		return orderEvent;
	}

	/**
	 * @description
	 *  Método de reificación de un modelo compatible anémico. Esto permite
	 *  ejecutar lógica de negocios sobre el modelo generado, siendo que el
	 *  valor de sus propiedades se mantiene.
	 *
	 * @param anemicOrderEvent
	 *  El modelo anémico a reificar.
	 *
	 * @returns
	 *  Devuelve un modelo de esta clase, cuyas propiedades fueron copiadas en
	 *  profundidad desde el modelo anémico.
	 */
	public static from(anemicOrderEvent: OrderEvent): OrderEvent {
		if (!anemicOrderEvent) return undefined;
		const orderEvent: OrderEvent = OrderEvent.empty();
		orderEvent.active = anemicOrderEvent.active;
		orderEvent.creationDate = new Date(anemicOrderEvent.creationDate);
		orderEvent.generateCreditNote = anemicOrderEvent.generateCreditNote;
		orderEvent.id = anemicOrderEvent.id;
		orderEvent.orderEventItems = TypeSystem.orElse(anemicOrderEvent.orderEventItems, [])
			.map(orderEventItem => OrderEventItems.from(orderEventItem));
		orderEvent.orderEventOriginType = anemicOrderEvent.orderEventOriginType;
		orderEvent.orderEventProducts = TypeSystem.orElse(anemicOrderEvent.orderEventProducts, [])
			.map(orderEventProduct => OrderEventProducts.from(orderEventProduct));
		orderEvent.orderEventResultId = anemicOrderEvent.orderEventResultId;
		orderEvent.orderEventStatus = anemicOrderEvent.orderEventStatus;
		orderEvent.orderEventTrackings = TypeSystem.orElse(anemicOrderEvent.orderEventTrackings, [])
			.map(orderEventTracking => OrderEventTracking.from(orderEventTracking));
		orderEvent.orderEventTypeId = anemicOrderEvent.orderEventTypeId;
		orderEvent.orderId = anemicOrderEvent.orderId;
		orderEvent.replacementItems = TypeSystem.orElse(anemicOrderEvent.replacementItems, [])
			.map(replacementItem => ReplacementItem.from(replacementItem));
		return orderEvent;
	}
}
